import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Typography,
  useTheme,
} from '@material-ui/core'
import { ProviderFragment } from '../../../generated/graphql'
import { Dialog } from '../../dialog/Dialog'
import { ControlledTextField } from '../../form/ControlledTextField'
import { validators } from '../../../validation/validators'
import type { MatrixDemand } from './UsageMatrixConnectionCell'
import { parseDecimal } from '../../../util/parsers'

type Props = {
  open: boolean
  onClose: () => void
  provider: ProviderFragment
  demand: MatrixDemand
  defaultValues?: Partial<FormValues>
  onEditAllocation: (
    provider: ProviderFragment,
    demandId: string,
    maxPortion: number,
  ) => void
}

type FormValues = {
  maxPortion: string
}

export function UsageMatrixConnectionDialog({
  open,
  onClose,
  provider,
  demand,
  defaultValues,
  onEditAllocation,
}: Props): React.ReactElement {
  const theme = useTheme()
  const { t } = useTranslation()

  const { control, handleSubmit, errors, reset } = useForm<FormValues>({
    defaultValues,
  })

  useEffect(() => {
    reset(defaultValues)
  }, [reset, defaultValues])

  const onSubmit = ({ maxPortion }: FormValues) => {
    onEditAllocation(provider, demand.id, parseDecimal(maxPortion))
    onClose()
  }

  return (
    <Dialog
      title={t('usageMatrix.restrictConnection')}
      open={open}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
            <ControlledTextField
              control={control}
              name="maxPortion"
              label={t('usageMatrix.restriction')}
              rules={{
                ...validators.required,
                ...validators.efficiency,
              }}
              error={errors.maxPortion}
            />

            <Typography variant="body1">
              {t('usageMatrix.restrictionExplanation', {
                providerName: provider.name,
                demandName: demand.name,
              })}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={onClose}>
            {t('common.cancel')}
          </Button>

          <Button
            id="connection-submit"
            type="submit"
            variant="contained"
            color="primary"
          >
            {t('usageMatrix.restrictConnection')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
